import { useState, useEffect, useCallback, useRef } from "react";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, TrendingUp, TrendingDown, Minus, Users, Clock, MapPin,
  Radio, Eye, Search, Gift, Star, Lock, RefreshCw, ChevronRight,
  AlertTriangle, Utensils, ArrowUpRight, ArrowDownRight, ClipboardList
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend
} from "recharts";
import TipOfTheDay from "@/components/shared/TipOfTheDay";

// ── Skeleton ──
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-white/5 rounded-lg ${className}`} />
);

const SkeletonCard = () => (
  <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
    <Skeleton className="h-4 w-20 mb-3" />
    <Skeleton className="h-8 w-16 mb-1" />
    <Skeleton className="h-3 w-24" />
  </div>
);

// ── Metric Card ──
const MetricCard = ({ icon: Icon, iconColor = "text-[#F59E0B]", value, label, sub, trend, testId }) => (
  <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid={testId}>
    <Icon className={`w-4 h-4 ${iconColor} mb-2`} />
    <p className="text-2xl font-black text-white">{value ?? "—"}</p>
    <p className="text-[11px] text-white/40 mt-0.5">{label}</p>
    {sub && <p className="text-[10px] text-white/30 mt-0.5">{sub}</p>}
    {trend !== undefined && trend !== null && (
      <div className={`flex items-center gap-1 mt-1 ${trend > 0 ? "text-emerald-400" : trend < 0 ? "text-red-400" : "text-white/30"}`}>
        {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : trend < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
        <span className="text-[10px] font-bold">{trend > 0 ? "+" : ""}{trend}%</span>
      </div>
    )}
  </div>
);

// ── Section Header ──
const SectionHeader = ({ title, subtitle }) => (
  <div className="mb-3 mt-6">
    <h2 className="text-sm font-bold text-white">{title}</h2>
    {subtitle && <p className="text-[10px] text-white/30 mt-0.5">{subtitle}</p>}
  </div>
);

// ── Error Section ──
const ErrorSection = ({ title, onRetry }) => (
  <div className="bg-[#2C2C2E] rounded-xl p-4 border border-red-500/20 text-center">
    <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-2" />
    <p className="text-xs text-white/60">Unable to load {title}</p>
    <button onClick={onRetry} className="mt-2 text-[10px] text-[#F59E0B] font-bold">Retry</button>
  </div>
);

// ── Locked Section (tier-gated) ──
const LockedSection = ({ data, testId }) => (
  <div className="bg-[#2C2C2E]/50 rounded-xl p-4 border border-white/5 opacity-50" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Lock className="w-4 h-4 text-white/20" />
      <span className="text-[10px] text-white/20 font-bold uppercase tracking-wider">
        {data.required_tier} Tier
      </span>
    </div>
    <p className="text-xs text-white/30 leading-relaxed">{data.upgrade_prompt}</p>
  </div>
);

// ── Funnel Bar ──
const FunnelBar = ({ stages }) => {
  const max = Math.max(...stages.map(s => s.value), 1);
  return (
    <div className="space-y-2 mt-3" data-testid="punch-funnel">
      {stages.map(s => (
        <div key={s.label} className="flex items-center gap-2">
          <span className="text-[10px] text-white/40 w-20 text-right">{s.label}</span>
          <div className="flex-1 bg-white/5 rounded-full h-3 overflow-hidden">
            <div className="h-full rounded-full bg-[#F59E0B]/60" style={{ width: `${Math.max((s.value / max) * 100, 2)}%` }} />
          </div>
          <span className="text-[10px] text-white/50 w-8">{s.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── Custom Tooltip ──
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1C1C1E] border border-white/10 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-[10px] text-white/40">{label}</p>
      <p className="text-sm font-bold text-[#F59E0B]">{payload[0].value}</p>
    </div>
  );
};

export default function VendorAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const refreshRef = useRef(null);
  const touchStartY = useRef(0);
  const [refreshing, setRefreshing] = useState(false);
  const [menuWishlist, setMenuWishlist] = useState([]);
  const [roi, setRoi] = useState(null);
  const [roiLoading, setRoiLoading] = useState(true);
  const [posData, setPosData] = useState({ square: null, spoton: null, connections: {} });
  const [surveyInsights, setSurveyInsights] = useState(null);
  const [tierStats, setTierStats] = useState(null);

  const fetchData = useCallback(async (showToast = false) => {
    try {
      setError(null);
      const res = await axios.get(`${API}/vendor/analytics`);
      setData(res.data);
      setLastUpdated(new Date());
      if (showToast) toast.success("Analytics refreshed");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchChart = useCallback(async () => {
    setChartLoading(true);
    try {
      const res = await axios.get(`${API}/vendor/analytics/history?metric=profile_views&period=30d`);
      setChartData((res.data?.data || []).map(d => ({
        date: d.date.slice(5),
        views: d.value,
      })));
    } catch { /* ignore */ }
    finally { setChartLoading(false); }
  }, []);

  useEffect(() => { fetchData(); fetchChart(); }, [fetchData, fetchChart]);

  // Fetch menu wishlist + ROI when we have data
  useEffect(() => {
    if (data?.business_id) {
      axios.get(`${API}/businesses/${data.business_id}/menu-wishlist`).then(res => {
        setMenuWishlist(res.data?.wishlist || []);
      }).catch(() => {});
    }
    // Fetch ROI summary
    axios.get(`${API}/vendor/roi-summary`).then(res => {
      setRoi(res.data);
    }).catch(() => {}).finally(() => setRoiLoading(false));

    // Fetch tier stats
    axios.get(`${API}/vendor/punch-tier-stats`).then(res => {
      setTierStats(res.data);
    }).catch(() => {});
    // Fetch POS data
    Promise.all([
      axios.get(`${API}/pos/connections`).catch(() => ({ data: {} })),
      axios.get(`${API}/pos/square/sync-data`).catch(() => ({ data: {} })),
      axios.get(`${API}/pos/spoton/sync-data`).catch(() => ({ data: {} })),
    ]).then(([connRes, sqRes, spRes]) => {
      setPosData({ connections: connRes.data, square: sqRes.data, spoton: spRes.data });
    });
    // Fetch survey insights
    if (data?.business_id) {
      axios.get(`${API}/survey/vendor-insights/${data.business_id}`).then(res => {
        setSurveyInsights(res.data);
      }).catch(() => {});
    }
  }, [data?.business_id]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    refreshRef.current = setInterval(() => { fetchData(); fetchChart(); }, 300000);
    return () => clearInterval(refreshRef.current);
  }, [fetchData, fetchChart]);

  // Pull to refresh
  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    const diff = e.changedTouches[0].clientY - touchStartY.current;
    if (diff > 100 && window.scrollY === 0) {
      setRefreshing(true);
      fetchData(true);
      fetchChart();
    }
  };

  const handleRefresh = () => { setRefreshing(true); fetchData(true); fetchChart(); };

  const trial = data?.trial;
  const profile = data?.profile;
  const live = data?.live_sessions;
  const punch = data?.punch_cards;
  const birthday = data?.birthday_offers;
  const community = data?.community;
  const discovery = data?.discovery;
  const engagement = data?.engagement;

  const formatHour = (h) => {
    if (h === null || h === undefined) return "—";
    const suffix = h >= 12 ? "PM" : "AM";
    const hr = h % 12 || 12;
    return `${hr} ${suffix}`;
  };

  // ── Skeleton Loading ──
  if (loading) {
    return (
      <div className="min-h-screen px-4 pt-14 pb-24" data-testid="vendor-analytics-skeleton">
        <Skeleton className="h-12 w-full mb-4" />
        <div className="grid grid-cols-2 gap-3">
          {[1,2,3,4].map(i => <SkeletonCard key={i} />)}
        </div>
        <Skeleton className="h-6 w-32 mt-6 mb-3" />
        <div className="grid grid-cols-2 gap-3">
          {[1,2].map(i => <SkeletonCard key={i} />)}
        </div>
        <Skeleton className="h-40 w-full mt-4" />
        <Skeleton className="h-6 w-40 mt-6 mb-3" />
        <div className="grid grid-cols-3 gap-3">
          {[1,2,3].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 pt-14 pb-24"
         onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
         data-testid="vendor-analytics-dashboard">
      <TipOfTheDay pageKey="vendor_analytics" />

      {/* Pull to refresh indicator */}
      {refreshing && (
        <div className="flex justify-center py-2">
          <RefreshCw className="w-4 h-4 text-[#F59E0B] animate-spin" />
        </div>
      )}

      {/* ── Section 1: Loyalty Card Performance ── */}
      <SectionHeader title="Loyalty Card Performance" />
      {punch?.locked ? <LockedSection data={punch} testId="punch-locked" /> : punch ? (
        <>
          <div className="grid grid-cols-3 gap-2" data-testid="punch-section">
            <MetricCard icon={Star} iconColor="text-[#F59E0B]" value={punch.unique_customers}
              label="Active loyalty cards" testId="metric-active-cards" />
            <MetricCard icon={Gift} iconColor="text-emerald-400" value={punch.total_redeemed}
              label="Rewards redeemed" testId="metric-redeemed" />
            <MetricCard icon={TrendingUp} iconColor="text-sky-400" value={`${punch.completion_rate_pct}%`}
              label="Completion rate" testId="metric-completion-rate" />
          </div>
          <FunnelBar stages={[
            { label: "Started", value: punch.total_punches },
            { label: "Halfway", value: Math.round(punch.total_punches * 0.5) },
            { label: "Completed", value: punch.total_redeemed + Math.round(punch.total_punches * 0.1) },
            { label: "Redeemed", value: punch.total_redeemed },
          ]} />
        </>
      ) : <ErrorSection title="punch cards" onRetry={handleRefresh} />}

      {/* ── Loyalty Tiers (Tier Distribution) ── */}
      {tierStats?.tier_enabled && (
        <>
          <SectionHeader title="Loyalty Tiers" />
          <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid="loyalty-tiers-card">
            <div className="space-y-2">
              {["1:Gray:#888888","2:Blue:#3a6ab0","3:Green:#2e7d32","4:Purple:#6a1b9a","5:Red:#c62828","6:Bronze:#8d5524","7:Silver:#9e9e9e","8:Gold:#daa520","9:Platinum:#c0c0e8"].map(item => {
                const [level, name, color] = item.split(":");
                const count = tierStats.tier_distribution?.[level] || 0;
                return (
                  <div key={level} className="flex items-center gap-2">
                    <span className="text-[10px] font-bold w-16 text-right" style={{ color }}>{name}</span>
                    <div className="flex-1 bg-white/5 rounded-full h-3 overflow-hidden">
                      <div className="h-full rounded-full" style={{ background: color, width: `${Math.max(count > 0 ? (count / Math.max(tierStats.total_tier_users || 1, 1)) * 100 : 0, count > 0 ? 5 : 0)}%` }} />
                    </div>
                    <span className="text-[10px] text-white/50 w-6">{count}</span>
                  </div>
                );
              })}
            </div>
            {tierStats.total_tier_users === 0 || Object.values(tierStats.tier_distribution || {}).every(v => v === 0 || !v) ? (
              <p className="text-[10px] text-white/30 mt-3 text-center">Your customers are just getting started. Keep them coming back.</p>
            ) : (((tierStats.tier_distribution?.["8"] || 0) + (tierStats.tier_distribution?.["9"] || 0)) > 0) ? (
              <p className="text-[10px] text-emerald-400 mt-3 text-center">You have {(tierStats.tier_distribution?.["8"] || 0) + (tierStats.tier_distribution?.["9"] || 0)} Gold+ level regulars. These are your most loyal customers.</p>
            ) : null}
          </div>
        </>
      )}

      {/* ── Section 2: Birthday Offer Performance ── */}
      <SectionHeader title="Birthday Offer Performance" />
      {birthday?.locked ? <LockedSection data={birthday} testId="birthday-locked" /> : birthday ? (
        <>
          <div className="grid grid-cols-2 gap-3" data-testid="birthday-section">
            <MetricCard icon={Eye} iconColor="text-violet-400" value={birthday.views_this_month}
              label="Offer views this month" testId="metric-bday-views" />
            <MetricCard icon={Gift} iconColor="text-emerald-400" value={birthday.claims_this_month}
              label="Claims this month" sub={`${birthday.claim_rate_pct}% claim rate`} testId="metric-bday-claims" />
          </div>
          <p className="text-[10px] text-white/20 mt-2 leading-relaxed px-1">
            Birthday celebrants rarely come alone. Each claim typically brings 2 to 5 paying guests.
          </p>
        </>
      ) : <ErrorSection title="birthday offers" onRetry={handleRefresh} />}

      {/* ── Section 3: Community Activity ── */}
      <SectionHeader title="Community Activity" />
      {community?.locked ? <LockedSection data={community} testId="community-locked" /> : community ? (
        <>
          <div className="grid grid-cols-2 gap-3" data-testid="community-section">
            <MetricCard icon={MapPin} iconColor="text-emerald-400" value={community.spots_this_month}
              label="Spotted this month" testId="metric-spots-month" />
            <MetricCard icon={Clock} iconColor="text-sky-400"
              value={community.avg_wait_minutes !== null ? `${community.avg_wait_minutes}m` : "—"}
              label="Avg reported wait" testId="metric-avg-wait" />
          </div>
          <p className="text-[10px] text-white/20 mt-2 leading-relaxed px-1">
            Your community is your marketing team. Every spot puts you in front of new customers.
          </p>
        </>
      ) : <ErrorSection title="community activity" onRetry={handleRefresh} />}

      {/* ── Customer Insights (Data Mining) ── */}
      {surveyInsights && surveyInsights.total_responses > 0 && (
        <>
          <SectionHeader title="Customer Insights" subtitle={`${surveyInsights.total_responses} survey responses from customers in line`} />
          <div className="space-y-3" data-testid="survey-insights-section">
            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-2">
              <MetricCard icon={Users} iconColor="text-sky-400" value={`${surveyInsights.first_time_visitors_pct}%`}
                label="First-time visitors" testId="metric-first-timers" />
              <MetricCard icon={Users} iconColor="text-emerald-400" value={surveyInsights.avg_group_size}
                label="Avg group size" testId="metric-avg-group" />
              <MetricCard icon={ClipboardList} iconColor="text-violet-400" value={surveyInsights.total_responses}
                label="Total responses" testId="metric-total-surveys" />
            </div>

            {/* Discovery Sources */}
            {surveyInsights.discovery_sources.length > 0 && (
              <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid="discovery-sources-survey">
                <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-3">How Customers Found You</p>
                <div className="space-y-2">
                  {surveyInsights.discovery_sources.map((d, i) => {
                    const maxCount = surveyInsights.discovery_sources[0]?.count || 1;
                    return (
                      <div key={d.source} className="flex items-center gap-2">
                        <span className="text-[10px] text-white/30 w-4 text-right">{i + 1}.</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs text-white/70">{d.source}</span>
                            <span className="text-[10px] text-[#F59E0B] font-bold">{d.count}</span>
                          </div>
                          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-[#F59E0B]/50 rounded-full" style={{ width: `${(d.count / maxCount) * 100}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Planned vs Spontaneous */}
            {(surveyInsights.planned_vs_spontaneous.planned > 0 || surveyInsights.planned_vs_spontaneous.spontaneous > 0) && (
              <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid="planned-vs-spontaneous">
                <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-3">Planned vs Spontaneous Visits</p>
                <div className="flex gap-3">
                  <div className="flex-1 text-center bg-[#1C1C1E] rounded-lg p-3">
                    <p className="text-lg font-black text-emerald-400">{surveyInsights.planned_vs_spontaneous.planned}</p>
                    <p className="text-[10px] text-white/40">Planned ahead</p>
                  </div>
                  <div className="flex-1 text-center bg-[#1C1C1E] rounded-lg p-3">
                    <p className="text-lg font-black text-sky-400">{surveyInsights.planned_vs_spontaneous.spontaneous}</p>
                    <p className="text-[10px] text-white/40">Spontaneous</p>
                  </div>
                </div>
              </div>
            )}

            {/* Top Menu Favorites */}
            {surveyInsights.top_menu_favorites.length > 0 && (
              <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid="menu-favorites-survey">
                <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-3">Most Popular Menu Items</p>
                <div className="space-y-1.5">
                  {surveyInsights.top_menu_favorites.map((item, i) => (
                    <div key={item.item} className="flex items-center gap-3">
                      <span className="text-[10px] text-white/20 w-5">{i + 1}.</span>
                      <span className="text-sm font-medium text-white capitalize flex-1">{item.item}</span>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Menu Wishlist */}
            {surveyInsights.top_menu_wishlist.length > 0 && (
              <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid="menu-wishlist-survey">
                <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-3">What Customers Want on Your Menu</p>
                <div className="space-y-1.5">
                  {surveyInsights.top_menu_wishlist.map((item, i) => (
                    <div key={item.item} className="flex items-center gap-3">
                      <span className="text-[10px] text-white/20 w-5">{i + 1}.</span>
                      <span className="text-sm font-medium text-white capitalize flex-1">{item.item}</span>
                      <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] font-bold">{item.count} requests</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Visit Frequency */}
            {surveyInsights.visit_frequency.length > 0 && (
              <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid="visit-frequency-survey">
                <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-3">How Often Customers Visit</p>
                <div className="space-y-1.5">
                  {surveyInsights.visit_frequency.map(f => (
                    <div key={f.frequency} className="flex items-center justify-between bg-[#1C1C1E] rounded-lg px-3 py-2">
                      <span className="text-xs text-white/60">{f.frequency}</span>
                      <span className="text-xs text-white font-bold">{f.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── ROI Summary ── */}
      {!roiLoading && roi && (
        <div className="mb-4" data-testid="roi-summary-section">
          <SectionHeader title="ROI Summary" subtitle="Your impact on BirthdayDeaLight" />

          {/* Three large metric cards */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-xl p-3 border border-emerald-500/10" data-testid="roi-customers">
              <p className="text-xl font-black text-emerald-400">{roi.estimated_customers_sent || 0}</p>
              <p className="text-[9px] text-white/50 font-medium leading-tight">Estimated customers sent</p>
              <p className="text-[8px] text-white/30 mt-0.5">Includes their friends & family</p>
            </div>
            <div className="bg-gradient-to-br from-[#F59E0B]/10 to-[#F59E0B]/5 rounded-xl p-3 border border-[#F59E0B]/10" data-testid="roi-revenue">
              <p className="text-sm font-black text-[#F59E0B] leading-tight">{roi.estimated_revenue_impact}</p>
              <p className="text-[9px] text-white/50 font-medium leading-tight mt-1">Revenue impact</p>
              <p className="text-[8px] text-white/30 mt-0.5">Based on typical group visit</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-xl p-3 border border-blue-500/10" data-testid="roi-rank">
              <p className="text-xl font-black text-blue-400">
                #{roi.competitive_visibility_score?.rank || "—"}
                <span className="text-[10px] text-white/30 font-normal">/{roi.competitive_visibility_score?.total_in_category || "—"}</span>
              </p>
              <p className="text-[9px] text-white/50 font-medium leading-tight">Visibility rank</p>
              <p className="text-[8px] text-white/30 mt-0.5 truncate">{roi.competitive_visibility_score?.percentile || ""}</p>
            </div>
          </div>

          {/* Discovery funnel */}
          {roi.discovery_funnel && (
            <div className="bg-[#2C2C2E] rounded-xl p-3 border border-white/5" data-testid="roi-funnel">
              <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-2">Discovery Funnel</p>
              <div className="flex items-center gap-1">
                {[
                  { label: "Search", value: roi.discovery_funnel.showed_in_search },
                  { label: "Profile Views", value: roi.discovery_funnel.viewed_profile },
                  { label: "Followers", value: roi.discovery_funnel.followed },
                  { label: "Claims", value: roi.discovery_funnel.claimed_offer },
                ].map((stage, i, arr) => (
                  <div key={stage.label} className="flex items-center gap-1 flex-1">
                    <div className="flex-1 text-center">
                      <p className="text-sm font-black text-white">{stage.value}</p>
                      <p className="text-[8px] text-white/30">{stage.label}</p>
                      {i < arr.length - 1 && stage.value > 0 && arr[i + 1].value > 0 && (
                        <p className="text-[7px] text-[#F59E0B] mt-0.5">
                          {Math.round((arr[i + 1].value / stage.value) * 100)}%
                        </p>
                      )}
                    </div>
                    {i < arr.length - 1 && (
                      <ChevronRight className="w-3 h-3 text-white/15 shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Section 1: Trial Status Banner ── */}
      {trial && (
        <TrialBanner trial={trial} onTap={() => navigate("/vendor/plan")} />
      )}

      {/* ── Section 2: Live Session Quick Stats ── */}
      <SectionHeader title="Live Sessions" />
      {live?.locked ? <LockedSection data={live} testId="live-sessions-locked" /> : live ? (
        <div className="grid grid-cols-2 gap-3" data-testid="live-sessions-section">
          <MetricCard icon={Radio} value={live.sessions_this_month} label="Sessions this month" testId="metric-sessions-month" />
          <MetricCard icon={Clock} value={`${live.total_hours_this_month}h`} label="Hours live this month" testId="metric-hours-month" />
          <MetricCard icon={Clock} iconColor="text-blue-400" value={formatHour(live.most_common_go_live_hour)} label="Most common go-live time" testId="metric-common-time" />
          <MetricCard icon={MapPin} iconColor="text-emerald-400" value={live.most_common_location || "—"} label="Most common location" testId="metric-common-location" />
        </div>
      ) : <ErrorSection title="live sessions" onRetry={handleRefresh} />}

      {/* ── Profile Performance ── */}
      <SectionHeader title="Profile Performance" />
      {profile?.locked ? <LockedSection data={profile} testId="profile-locked" /> : profile ? (
        <>
          <div className="grid grid-cols-2 gap-3" data-testid="profile-section">
            <MetricCard icon={Eye} value={profile.views_this_month} label="Views this month"
              trend={profile.mom_change_pct} testId="metric-views-month" />
            <MetricCard icon={Users} iconColor="text-sky-400" value={profile.total_followers} label="Total followers"
              sub={profile.new_followers_this_month > 0 ? `+${profile.new_followers_this_month} new this month` : null} testId="metric-followers" />
          </div>
          {/* Line chart */}
          <div className="bg-[#2C2C2E] rounded-xl p-3 mt-3 border border-white/5" data-testid="profile-views-chart">
            <p className="text-[10px] text-white/30 mb-2">Daily profile views — last 30 days</p>
            {chartLoading ? <Skeleton className="h-32 w-full" /> : (
              <ResponsiveContainer width="100%" height={130}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 9 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 9 }} tickLine={false} axisLine={false} width={20} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line type="monotone" dataKey="views" stroke="#F59E0B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      ) : <ErrorSection title="profile performance" onRetry={handleRefresh} />}

      {/* ── Section 7: How customers are finding you ── */}
      <SectionHeader title="How customers are finding you" />
      {discovery?.locked ? <LockedSection data={discovery} testId="discovery-locked" /> : discovery ? (
        <>
          <div className="grid grid-cols-3 gap-2" data-testid="discovery-section">
            <MetricCard icon={Search} iconColor="text-sky-400" value={discovery.search_appearances}
              label="Search results" testId="metric-search-appearances" />
            <MetricCard icon={BarChart3} iconColor="text-violet-400" value={discovery.discover_appearances}
              label="Discover feed" testId="metric-discover-appearances" />
            <MetricCard icon={Utensils} iconColor="text-emerald-400" value={discovery.unmet_demand}
              label="Unmet demand" testId="metric-unmet-demand" />
          </div>
          <p className="text-[10px] text-white/20 mt-2 leading-relaxed px-1">
            These are potential customers actively looking for what you offer. Going live more often in your area captures this demand.
          </p>
        </>
      ) : <ErrorSection title="discovery intelligence" onRetry={handleRefresh} />}

      {/* ── Section 7b: What Your Customers Want ── */}
      {menuWishlist.length > 0 && (
        <>
          <SectionHeader title="What Your Customers Want" />
          <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid="menu-wishlist-section">
            <Utensils className="w-4 h-4 text-[#F59E0B] mb-2" />
            <p className="text-[11px] text-white/40 mb-3">Top requested items from proximity surveys</p>
            <div className="space-y-2">
              {menuWishlist.map((item, i) => (
                <div key={item.item} className="flex items-center gap-3">
                  <span className="text-[10px] text-white/20 w-5">{i + 1}.</span>
                  <span className="text-sm font-medium text-white capitalize flex-1">{item.item}</span>
                  <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] font-bold">{item.count} mentions</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Section 8: Office Density ── */}
      <SectionHeader title="Your area intelligence" />
      {engagement?.locked ? <LockedSection data={engagement} testId="engagement-locked" /> : (
        <>
          {engagement?.office_density_score !== null && engagement?.office_density_score !== undefined ? (
            <div data-testid="office-density-section">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
                  <MapPin className="w-4 h-4 text-[#F59E0B] mb-2" />
                  <p className="text-2xl font-black text-white" data-testid="office-workers-count">{(engagement.office_density_workers ?? 0).toLocaleString()}</p>
                  <p className="text-[10px] text-white/40">Office workers within 0.5 miles</p>
                </div>
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
                  <Users className="w-4 h-4 text-[#F59E0B] mb-2" />
                  <p className={`text-xl font-black ${
                    engagement.office_density_category === "very_high" ? "text-red-400" :
                    engagement.office_density_category === "high" ? "text-orange-400" :
                    engagement.office_density_category === "medium" ? "text-amber-400" :
                    "text-emerald-400"
                  }`} data-testid="office-density-category">
                    {(engagement.office_density_category || "low").replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </p>
                  <p className="text-[10px] text-white/40">Density category</p>
                </div>
              </div>
              <p className="text-[10px] text-white/20 mt-3 px-1" data-testid="peak-hours-static">
                {engagement.peak_hours_text || "Peak lunch demand in your area is highest between 11:30 AM and 1:30 PM on weekdays."}
              </p>
            </div>
          ) : (
            <div className="bg-[#2C2C2E]/50 rounded-xl p-4 border border-white/5 opacity-50" data-testid="office-density-locked">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-white/20" />
                <span className="text-[10px] text-white/20 font-bold uppercase tracking-wider">Coming Soon</span>
              </div>
              <p className="text-lg font-bold text-white/20">Office density data</p>
              <p className="text-[10px] text-white/15 mt-1">Know how many office workers are near your home base for optimal positioning.</p>
            </div>
          )}

          {/* Engagement Score (compact) */}
          {engagement?.score !== null && engagement?.score !== undefined && (
            <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5 mt-3 flex items-center gap-3" data-testid="engagement-score">
              <div className="w-10 h-10 rounded-full bg-[#F59E0B]/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <div>
                <p className="text-lg font-black text-white">{engagement.score}</p>
                <p className="text-[10px] text-white/40">Engagement Score</p>
              </div>
              <div className="ml-auto">
                {engagement.trend === "up" && <TrendingUp className="w-4 h-4 text-emerald-400" />}
                {engagement.trend === "down" && <TrendingDown className="w-4 h-4 text-red-400" />}
                {engagement.trend === "stable" && <Minus className="w-4 h-4 text-white/30" />}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── POS Performance Section ── */}
      {(posData.connections?.square?.connected || posData.connections?.spoton?.connected) && (
        <div className="mt-4" data-testid="pos-performance-section">
          <SectionHeader title="POS Performance" />

          {/* Square connected indicator */}
          {posData.connections?.square?.connected && (
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-[10px] text-emerald-400 font-medium">Square Connected</span>
              {posData.connections.square.last_sync_at && (
                <span className="text-[9px] text-white/20">
                  Last synced {new Date(posData.connections.square.last_sync_at).toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          {/* Square metrics */}
          {posData.square?.total_transaction_count_this_month != null && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
                  <p className="text-2xl font-black text-white" data-testid="pos-txn-count">
                    {(posData.square.total_transaction_count_this_month || 0).toLocaleString()}
                  </p>
                  <p className="text-[10px] text-white/40">Transactions this month</p>
                </div>
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
                  <p className="text-xl font-black text-[#F59E0B]" data-testid="pos-busiest-day">
                    {posData.square.busiest_day || "—"}
                  </p>
                  <p className="text-[10px] text-white/40">Busiest day</p>
                </div>
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
                  <p className="text-xl font-black text-white" data-testid="pos-busiest-hour">
                    {posData.square.busiest_hour != null
                      ? `${posData.square.busiest_hour > 12 ? posData.square.busiest_hour - 12 : posData.square.busiest_hour}${posData.square.busiest_hour >= 12 ? "PM" : "AM"}`
                      : "—"}
                  </p>
                  <p className="text-[10px] text-white/40">Busiest hour</p>
                </div>
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
                  <p className="text-xl font-black text-emerald-400" data-testid="pos-bf-pct">
                    {data?.community_activity?.estimated_customers_sent && posData.square.total_transaction_count_this_month
                      ? `${((data.community_activity.estimated_customers_sent / posData.square.total_transaction_count_this_month) * 100).toFixed(1)}%`
                      : "—"}
                  </p>
                  <p className="text-[10px] text-white/40">BF contribution</p>
                </div>
              </div>

              {/* Overlay chart — Square transactions by hour vs BF spots by hour */}
              {posData.square.transaction_count_by_hour && (
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5" data-testid="pos-overlay-chart">
                  <p className="text-[10px] text-white/40 mb-3 font-bold uppercase tracking-wider">Square vs BirthdayDeaLight by Hour</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={Object.entries(posData.square.transaction_count_by_hour).map(([h, v]) => ({
                      hour: `${parseInt(h) > 12 ? parseInt(h) - 12 : parseInt(h)}${parseInt(h) >= 12 ? "p" : "a"}`,
                      square: v,
                      bf: Math.floor(v * 0.15),
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#666" }} />
                      <YAxis tick={{ fontSize: 9, fill: "#666" }} />
                      <Tooltip contentStyle={{ backgroundColor: "#1C1C1E", border: "1px solid #333", borderRadius: 8, fontSize: 10 }} />
                      <Legend wrapperStyle={{ fontSize: 9 }} />
                      <Bar dataKey="square" fill="#6366F1" name="Square" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="bf" fill="#F59E0B" name="BF Spots" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}

          {/* SpotOn connected — loyalty comparison */}
          {posData.connections?.spoton?.connected && posData.spoton?.repeat_customer_rate != null && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-[10px] text-emerald-400 font-medium">SpotOn Connected</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
                  <p className="text-2xl font-black text-white" data-testid="spoton-repeat-rate">
                    {posData.spoton.repeat_customer_rate != null ? `${posData.spoton.repeat_customer_rate}%` : "—"}
                  </p>
                  <p className="text-[10px] text-white/40">SpotOn repeat rate</p>
                </div>
                <div className="bg-[#2C2C2E] rounded-xl p-4 border border-white/5">
                  <p className="text-2xl font-black text-[#F59E0B]" data-testid="bf-repeat-rate">
                    {data?.punch_cards?.completion_rate != null ? `${data.punch_cards.completion_rate}%` : "—"}
                  </p>
                  <p className="text-[10px] text-white/40">BF repeat rate</p>
                </div>
              </div>
              <p className="text-[10px] text-white/20 mt-2 px-1">
                BirthdayDeaLight customers return at a higher rate than your average customer — your free birthday offer drives repeat business.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Last updated + manual refresh */}
      <div className="flex items-center justify-center gap-2 mt-6 mb-2">
        <button onClick={handleRefresh} className="text-[10px] text-white/20 hover:text-white/40 flex items-center gap-1 transition" data-testid="manual-refresh">
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
        {lastUpdated && (
          <span className="text-[10px] text-white/15">
            Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Trial Status Banner Component ──
function TrialBanner({ trial, onTap }) {
  if (!trial) return null;

  const { days_remaining, trial_expired, vendor_tier, trial_active } = trial;

  // Founding vendor badge
  const FoundingBadge = () => (
    vendor_tier === "poc" ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 text-[9px] text-[#F59E0B] font-bold ml-2">
        <Star className="w-2.5 h-2.5" /> Founding Vendor — POC Rate
      </span>
    ) : null
  );

  if (trial_expired) {
    return (
      <button onClick={onTap} className="w-full bg-red-500/15 border border-red-500/30 rounded-xl p-3 text-left mb-2" data-testid="trial-banner-expired">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-red-400">Your trial has ended</p>
            <p className="text-[10px] text-red-300/60 mt-0.5">Tap to choose your plan and keep full access.</p>
          </div>
          <ChevronRight className="w-4 h-4 text-red-400" />
        </div>
      </button>
    );
  }

  if (trial_active && days_remaining <= 30) {
    return (
      <button onClick={onTap} className="w-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-3 text-left mb-2" data-testid="trial-banner-warning">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-[#F59E0B]">Your free trial ends in {days_remaining} days</p>
            <p className="text-[10px] text-[#F59E0B]/60 mt-0.5">Your introductory POC rate locks in when it ends. Tap to see plan options.</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#F59E0B]" />
        </div>
        <FoundingBadge />
      </button>
    );
  }

  // Active, more than 30 days
  return (
    <button onClick={onTap} className="flex items-center gap-2 mb-2 hover:opacity-80 transition" data-testid="trial-banner-active">
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        <span className="text-[10px] text-emerald-400 font-bold">Trial Active — {days_remaining} days remaining</span>
      </span>
      <FoundingBadge />
    </button>
  );
}
